import { useCallback, useMemo, useState } from 'react'
import { Inbox } from 'lucide-react'
import {
  Alert,
  Button,
  Badge,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  useClickable,
  FormattedDate,
  PageHeader,
} from '@ideon-api/ideon-ui'
import { useAuthServiceClient } from '../lib/sdk-client'
import { useSdkList } from '../lib/use-sdk-list'
import { ReviewDuplicateDrawer, SIGNAL_LABEL, type DuplicateCandidate } from './duplicate-accounts/ReviewDuplicateDrawer'

function CandidateRow({ c, onSelect }: { c: DuplicateCandidate; onSelect: (c: DuplicateCandidate) => void }) {
  const clickable = useClickable(() => onSelect(c))
  return (
    <TableRow className="border-ideon-gray-30 cursor-pointer hover:bg-ideon-gray-10" {...clickable}>
      <TableCell>
        <div className="font-semibold text-ideon-gray-black">{c.account.name}</div>
        <div className="text-sm text-ideon-gray-70">{c.account.slug}</div>
      </TableCell>
      <TableCell>
        <div className="font-semibold text-ideon-gray-black">
          {c.duplicateAccount.name}
        </div>
        <div className="text-sm text-ideon-gray-70">{c.duplicateAccount.slug}</div>
      </TableCell>
      <TableCell>
        <Badge
          variant={c.kind === 'identifier_conflict' ? 'warning' : 'neutral'}
          dot={false}
          uppercase={false}
        >
          {SIGNAL_LABEL[c.kind]}
        </Badge>
      </TableCell>
      <TableCell className="text-ideon-gray-black">
        {c.confidence == null ? '—' : `${Math.round(c.confidence * 100)}%`}
      </TableCell>
      <TableCell>
        <FormattedDate value={c.lastDetectedAt} />
      </TableCell>
    </TableRow>
  )
}

export function DuplicateAccountsPage() {
  const getClient = useAuthServiceClient()
  const fetchCandidates = useCallback(async () => {
    const client = await getClient()
    return client.auth.listDuplicateCandidates()
  }, [getClient])
  const state = useSdkList<DuplicateCandidate>(fetchCandidates)
  const [selected, setSelected] = useState<DuplicateCandidate | null>(null)
  // Dismissed/merged candidates disappear from the list immediately, without
  // waiting on the reload -- then the reload also drops any sibling candidate
  // that shared an account with the one just acted on (e.g. a merge archives
  // the loser, invalidating every other pair referencing it server-side).
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set())

  const visibleRows = useMemo(
    () => (state.status === 'ready' ? state.data.filter((c) => !removedIds.has(c.id)) : []),
    [state, removedIds]
  )

  const handleRemoved = (candidateId: string) => {
    setRemovedIds((prev) => new Set(prev).add(candidateId))
    state.reload()
  }

  return (
    <>
      <PageHeader title="Duplicate accounts" subtitle="Account pairs flagged as possibly the same customer." />

      <div className="flex flex-col gap-6">
        {state.status === 'loading' && (
          <Table density="compact" variant="bordered" className="bg-white">
            <TableHeader>
              <TableRow className="border-ideon-gray-30">
                <TableHead className="text-ideon-gray-70">Account A</TableHead>
                <TableHead className="text-ideon-gray-70">Account B</TableHead>
                <TableHead className="text-ideon-gray-70">Signal</TableHead>
                <TableHead className="text-ideon-gray-70">Confidence</TableHead>
                <TableHead className="text-ideon-gray-70">Detected</TableHead>
              </TableRow>
            </TableHeader>
            <TableSkeleton columns={5} />
          </Table>
        )}
        {state.status === 'error' && (
          <Alert variant="error" action={<Button onClick={state.reload}>Retry</Button>}>
            Failed to load: {state.message}
          </Alert>
        )}
        {state.status === 'ready' && visibleRows.length === 0 && (
          <EmptyState className="bg-white" icon={<Inbox className="h-10 w-10" />} heading="No duplicate candidates right now" />
        )}
        {state.status === 'ready' && visibleRows.length > 0 && (
          <Table density="compact" variant="bordered" className="bg-white">
            <TableHeader>
              <TableRow className="border-ideon-gray-30">
                <TableHead className="text-ideon-gray-70">Account A</TableHead>
                <TableHead className="text-ideon-gray-70">Account B</TableHead>
                <TableHead className="text-ideon-gray-70">Signal</TableHead>
                <TableHead className="text-ideon-gray-70">Confidence</TableHead>
                <TableHead className="text-ideon-gray-70">Detected</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map((c) => (
                <CandidateRow key={c.id} c={c} onSelect={setSelected} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ReviewDuplicateDrawer
        candidate={selected}
        onClose={() => setSelected(null)}
        onDismissed={handleRemoved}
        onMerged={handleRemoved}
      />
    </>
  )
}
